import { useWriteStatusQuery } from 'src/api/queries/subscriptionQueries';

export type TWritesPaused = {
  /** True while the server refuses uploads and processing for this account. */
  paused: boolean;
  /** The server's reason, e.g. `subscription_payment_required`,
   *  `spend_cap_reached`, `trial_limit_reached`. Null when not paused. */
  reason: string | null;
};

/**
 * Whether the account is refusing writes right now — a trial that ended
 * without a card (`payment_required`), an expired, suspended or over-quota
 * account, or one at its spend limit. Every write would 503, so write actions
 * should be disabled and `WritesPausedBanner` shown instead.
 *
 * Reads `GET /api/v1/account/write-status`, which every member can read —
 * `GET /subscription` is owner/admin-only, so gating on it left members with
 * buttons that 503.
 *
 * A DORMANT account is not paused here even though the server reports it
 * blocked: its next write wakes it (the published "reactivates on any billable
 * action" mechanic), so disabling the buttons would make it impossible to wake.
 *
 * Fails open: while loading, or if the request fails, nothing is disabled —
 * the server's gate still has the final word.
 */
export function useWritesPaused(): TWritesPaused {
  const { data } = useWriteStatusQuery();
  const paused = Boolean(data?.write_blocked && !data?.dormant);
  return { paused, reason: paused ? data?.reason ?? null : null };
}
